import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { TimezoneProvider } from "@/contexts/TimezoneContext";
import { AiTasksProvider } from "@/contexts/AiTasksContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminAppLayout } from "@/components/layout/AdminAppLayout";
import { NetworkStatus } from "@/components/NetworkStatus";

// Lazy load all page components for code splitting
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Index = lazy(() => import("./pages/Index"));
const Leads = lazy(() => import("./pages/Leads"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const LeadImport = lazy(() => import("./pages/LeadImport"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientProjectDetail = lazy(() => import("./pages/ClientProjectDetail"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const Planning = lazy(() => import("./pages/Planning"));
const PlanningDay = lazy(() => import("./pages/PlanningDay"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Dossiers = lazy(() => import("./pages/Dossiers"));
const DossierDetail = lazy(() => import("./pages/DossierDetail"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CeeSheets = lazy(() => import("./pages/CeeSheets"));
const CeeSheetDetail = lazy(() => import("./pages/CeeSheetDetail"));
const AnalysisReview = lazy(() => import("./pages/AnalysisReview"));
const Documents = lazy(() => import("./pages/Documents"));
const Training = lazy(() => import("./pages/Training"));
const CourseView = lazy(() => import("./pages/CourseView"));
const SalesHub = lazy(() => import("./pages/SalesHub"));
const Calculator = lazy(() => import("./pages/Calculator"));
const Finances = lazy(() => import("./pages/Finances"));
const SalesModeView = lazy(() => import("./pages/SalesModeView"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsersTeams = lazy(() => import("./pages/admin/UsersTeams"));
const AdminUserDetail = lazy(() => import("./pages/admin/UserDetail"));
const AdminImports = lazy(() => import("./pages/admin/Imports"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminProductDetail = lazy(() => import("./pages/admin/ProductDetail"));
const AdminCeeSheets = lazy(() => import("./pages/admin/CeeSheets"));
const AdminFinanceConfig = lazy(() => import("./pages/admin/FinanceConfig"));
const AdminSalesEnablement = lazy(() => import("./pages/admin/SalesEnablement"));
const AdminTraining = lazy(() => import("./pages/admin/Training"));
const AdminPlanning = lazy(() => import("./pages/admin/Planning"));
const AdminFinance = lazy(() => import("./pages/admin/Finance"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const AdminAiConfig = lazy(() => import("./pages/admin/AiConfig"));
const AdminObjectives = lazy(() => import("./pages/admin/Objectives"));
const AdminAutomations = lazy(() => import("./pages/admin/Automations"));
const AdminChatSupport = lazy(() => import("./pages/admin/ChatSupport"));
const AdminClientPortals = lazy(() => import("./pages/admin/ClientPortals"));
const AdminPartners = lazy(() => import("./pages/admin/Partners"));

const queryClient = new QueryClient();

// Minimal loading fallback - just a subtle spinner
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('Failed to fetch dynamically imported module') ||
          event.reason?.message?.includes('Importing a module script failed')) {
        event.preventDefault();
        window.location.reload();
      }
    };
    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <TimezoneProvider>
        <TooltipProvider>
          <NetworkStatus />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <AiTasksProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />

                {/* Sales Workspace */}
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/leads" element={<Leads />} />
                  <Route path="/leads/import" element={<LeadImport />} />
                  <Route path="/leads/:id" element={<LeadDetail />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/clients/:id" element={<ClientDetail />} />
                  <Route path="/clients/project/:id" element={<ClientProjectDetail />} />
                  <Route path="/planning" element={<Planning />} />
                  <Route path="/planning/day" element={<PlanningDay />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/dossiers" element={<Dossiers />} />
                  <Route path="/dossiers/:id" element={<DossierDetail />} />
                  <Route path="/aides" element={<SalesHub />} />
                  <Route path="/calculatrice" element={<Calculator />} />
                  <Route path="/finances" element={<Finances />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/cee-sheets" element={<CeeSheets />} />
                  <Route path="/cee-sheets/:id" element={<CeeSheetDetail />} />
                  <Route path="/cee-sheets/:id/analysis-review" element={<AnalysisReview />} />
                  <Route path="/documents" element={<Documents />} />
                  <Route path="/training" element={<Training />} />
                  <Route path="/training/:id" element={<CourseView />} />
                </Route>

                {/* Full-screen Sales Mode (no sidebar) */}
                <Route path="/leads/:id/mode-vente" element={<SalesModeView />} />

                {/* Public Client Portal (no auth required) */}
                <Route path="/portail/:token" element={<ClientPortal />} />

                {/* Admin Workspace */}
                <Route element={<AdminAppLayout />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/users" element={<AdminUsersTeams />} />
                  <Route path="/admin/users/:id" element={<AdminUserDetail />} />
                  <Route path="/admin/imports" element={<AdminImports />} />
                  <Route path="/admin/products" element={<AdminProducts />} />
                  <Route path="/admin/products/:id" element={<AdminProductDetail />} />
                  <Route path="/admin/cee-sheets" element={<AdminCeeSheets />} />
                  <Route path="/admin/finance-config" element={<AdminFinanceConfig />} />
                  <Route path="/admin/sales-enablement" element={<AdminSalesEnablement />} />
                  <Route path="/admin/training" element={<AdminTraining />} />
                  <Route path="/admin/planning" element={<AdminPlanning />} />
                  <Route path="/admin/finance" element={<AdminFinance />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/ai-config" element={<AdminAiConfig />} />
                  <Route path="/admin/audit" element={<AuditLog />} />
                  <Route path="/admin/objectives" element={<AdminObjectives />} />
                  <Route path="/admin/automations" element={<AdminAutomations />} />
                  <Route path="/admin/chat-support" element={<AdminChatSupport />} />
                  <Route path="/admin/client-portals" element={<AdminClientPortals />} />
                  <Route path="/admin/partners" element={<AdminPartners />} />
                </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </AiTasksProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </TimezoneProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
